import { useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { CopyButton } from './CopyButton';
import { ErrorState, LoadingState } from './RequestState';
import { PayloadViewer } from './PayloadViewer';
import { PageHeader, type FeatureStatus } from './FeatureStatus';
import { ConfirmDialog, type ConfirmDialogState } from './ConfirmDialog';
import { RelatedLinks } from './RelatedLinks';
import { Timeline } from './Timeline';
import { ValidationReportView } from './ValidationReportView';
import { useAsyncData } from '../hooks/useAsyncData';
import { as2InboundTransportByCorrelationLink, exchangesByCorrelationLink, executionsByCorrelationLink } from '../lib/crossLinks';
import { buildCustomsTimelineEvents, type ValidationReportSection } from '../lib/customsMessages';
import type { ApiListResponse } from '../types/api';

// Shared shell for the ADII/PortNet customs-message consoles (CUSDEC ML, Etat de Chargement,
// Tryptique — see apps/integration/docs/frontend-ux-audit-2026-08.md §5, Top Opportunity #7).
// All three were independent, near-identical implementations of the same list+inline-detail
// pattern (filter bar, paginated table, XML toggle, normalized JSON, timeline) with per-type
// differences only in filter fields, list columns, summary fields, child tables, validation
// report shape, and whether operator actions (reprocess/replay) exist at all.
//
// This is a slot/composition API, not a giant conditional: a new customs message type is added
// by writing a new page that supplies its own filter/column/summary/child-table/validation
// functions, not by adding a branch inside this file. `detailActions` is the one slot that's
// entirely optional — omitting it (as Etat de Chargement/Tryptique do) is what keeps those two
// consoles honestly read-only; the shell never fabricates a Reprocess/Replay button on its own.

export interface CustomsMessageDetailBase {
  id: string;
  correlationId: string;
  processingError: string | null;
  normalizedJson: Record<string, unknown> | null;
  timeline: Array<{ at: string; status: string; detail?: string }>;
}

export interface CustomsMessageListColumn<TListItem> {
  header: string;
  render: (item: TListItem) => ReactNode;
}

export interface CustomsMessageDetailActionHelpers {
  refreshDetail: () => Promise<void>;
  onListChanged: () => void;
  setConfirm: (state: ConfirmDialogState | null) => void;
  setActionError: (message: string | null) => void;
}

export interface CustomsMessageConsoleProps<
  TListItem extends { id: string },
  TFilters extends { page?: number; pageSize?: number },
  TDetail extends CustomsMessageDetailBase,
> {
  eyebrow: string;
  title: string;
  purpose: string;
  status: FeatureStatus;
  next: string;
  listPanelTitle: string;
  primaryColumnHeader: string;
  emptyRowLabel: string;
  detailTitle: string;
  initialFilters: TFilters;
  pageSize?: number;
  listFetch: (filters: TFilters) => Promise<ApiListResponse<TListItem>>;
  detailFetch: (id: string) => Promise<TDetail>;
  rawXmlFetch?: (id: string) => Promise<string>;
  filterFields: (filters: TFilters, patchFilters: (patch: Partial<TFilters>) => void) => ReactNode;
  listColumns: Array<CustomsMessageListColumn<TListItem>>;
  getRowId: (item: TListItem) => string;
  getRowLabel: (item: TListItem) => ReactNode;
  detailSummaryFields: (detail: TDetail) => ReactNode;
  detailChildTables?: (detail: TDetail) => ReactNode;
  detailValidationReport?: (detail: TDetail) => ValidationReportSection[];
  detailActions?: (detail: TDetail, helpers: CustomsMessageDetailActionHelpers) => ReactNode;
}

// Reused by every page's detailSummaryFields slot — all three consoles show correlation ID as
// the last summary-grid row, in the same copyable format. Reads location itself (rather than
// taking `search` as a prop) so the three call sites don't need to thread it through the shell's
// detailSummaryFields slot signature, which only receives `detail`.
export function CorrelationIdField({ value }: { value: string }) {
  const { search } = useLocation();
  return (
    <div>
      <strong>Correlation ID</strong>
      <span className="inline-copy">
        <span className="mono">{value}</span>
        <CopyButton value={value} label="Copy correlation ID" />
        <RelatedLinks links={[
          as2InboundTransportByCorrelationLink(value, search),
          exchangesByCorrelationLink(value, search),
          executionsByCorrelationLink(value, search),
        ]}
        />
      </span>
    </div>
  );
}

export function CustomsMessageConsole<
  TListItem extends { id: string },
  TFilters extends { page?: number; pageSize?: number },
  TDetail extends CustomsMessageDetailBase,
>(props: CustomsMessageConsoleProps<TListItem, TFilters, TDetail>) {
  const pageSize = props.pageSize ?? 20;
  const [filters, setFilters] = useState<TFilters>(props.initialFilters);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { data, isLoading, error, refresh } = useAsyncData(
    () => props.listFetch(filters),
    [JSON.stringify(filters)],
  );

  const total = data?.total ?? 0;
  const page = filters.page ?? 1;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  function patchFilters(patch: Partial<TFilters>) {
    setFilters((current) => ({ ...current, ...patch, page: 1 }));
  }

  return (
    <div className="page-grid">
      <PageHeader
        eyebrow={props.eyebrow}
        title={props.title}
        purpose={props.purpose}
        status={props.status}
        connected
        next={props.next}
      />
      <section className="panel">
        <div className="panel-header">
          <h2>{props.listPanelTitle}</h2>
          <div className="panel-actions">
            <button type="button" onClick={() => void refresh()}>Refresh</button>
          </div>
        </div>
        <div className="toolbar toolbar-grid">
          {props.filterFields(filters, patchFilters)}
          <button type="button" className="ghost-button" onClick={() => setFilters(props.initialFilters)}>Reset</button>
        </div>
        {isLoading ? <LoadingState /> : null}
        {error ? <ErrorState message={error} onRetry={refresh} /> : null}
        {!isLoading && !error ? (
          <table className="data-table">
            <thead>
              <tr>
                <th>{props.primaryColumnHeader}</th>
                {props.listColumns.map((column) => <th key={column.header}>{column.header}</th>)}
              </tr>
            </thead>
            <tbody>
              {(data?.data ?? []).map((item) => (
                <tr key={props.getRowId(item)}>
                  <td>
                    <button type="button" className="link-button" onClick={() => setSelectedId(props.getRowId(item))}>
                      <span className="mono">{props.getRowLabel(item)}</span>
                    </button>
                  </td>
                  {props.listColumns.map((column) => <td key={column.header}>{column.render(item)}</td>)}
                </tr>
              ))}
              {(data?.data ?? []).length === 0 ? (
                <tr><td colSpan={props.listColumns.length + 1}>{props.emptyRowLabel}</td></tr>
              ) : null}
            </tbody>
          </table>
        ) : null}
        {total > pageSize ? (
          <div className="pagination-bar">
            <button
              type="button"
              className="ghost-button"
              disabled={page <= 1 || isLoading}
              onClick={() => setFilters((current) => ({ ...current, page: page - 1 }))}
            >
              Previous
            </button>
            <span>Page {page} of {totalPages} &mdash; {total} total</span>
            <button
              type="button"
              className="ghost-button"
              disabled={page >= totalPages || isLoading}
              onClick={() => setFilters((current) => ({ ...current, page: page + 1 }))}
            >
              Next
            </button>
          </div>
        ) : null}
      </section>

      {selectedId ? (
        <CustomsMessageDetailPanel
          key={selectedId}
          id={selectedId}
          detailTitle={props.detailTitle}
          detailFetch={props.detailFetch}
          rawXmlFetch={props.rawXmlFetch}
          detailSummaryFields={props.detailSummaryFields}
          detailChildTables={props.detailChildTables}
          detailValidationReport={props.detailValidationReport}
          detailActions={props.detailActions}
          onClose={() => setSelectedId(null)}
          onListChanged={() => refresh()}
        />
      ) : null}
    </div>
  );
}

function CustomsMessageDetailPanel<TDetail extends CustomsMessageDetailBase>({
  id,
  detailTitle,
  detailFetch,
  rawXmlFetch,
  detailSummaryFields,
  detailChildTables,
  detailValidationReport,
  detailActions,
  onClose,
  onListChanged,
}: {
  id: string;
  detailTitle: string;
  detailFetch: (id: string) => Promise<TDetail>;
  rawXmlFetch?: (id: string) => Promise<string>;
  detailSummaryFields: (detail: TDetail) => ReactNode;
  detailChildTables?: (detail: TDetail) => ReactNode;
  detailValidationReport?: (detail: TDetail) => ValidationReportSection[];
  detailActions?: (detail: TDetail, helpers: CustomsMessageDetailActionHelpers) => ReactNode;
  onClose: () => void;
  onListChanged: () => void;
}) {
  const [confirm, setConfirm] = useState<ConfirmDialogState | null>(null);
  const [rawXml, setRawXml] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const { data, isLoading, error, refresh } = useAsyncData(() => detailFetch(id), [id]);

  async function handleToggleRawXml() {
    if (!rawXmlFetch) return;
    if (rawXml !== null) {
      setRawXml(null);
      return;
    }
    try {
      setActionError(null);
      setRawXml(await rawXmlFetch(id));
    } catch (caught) {
      setActionError(caught instanceof Error ? caught.message : 'Unable to load raw XML.');
    }
  }

  if (isLoading) return <LoadingState />;
  if (error) return <ErrorState message={error} onRetry={refresh} />;
  if (!data) return null;

  const validationSections = detailValidationReport?.(data) ?? [];
  const hasValidationContent = validationSections.some((section) => section.items.length > 0);
  const timelineEvents = buildCustomsTimelineEvents(data.timeline);

  return (
    <section className="panel">
      <div className="panel-header">
        <div>
          <h2>{detailTitle}</h2>
          <p className="mono">{data.id}</p>
        </div>
        <div className="panel-actions">
          {detailActions?.(data, {
            refreshDetail: async () => { setRawXml(null); await refresh(); },
            onListChanged,
            setConfirm,
            setActionError,
          })}
          {rawXmlFetch ? (
            <button type="button" onClick={() => void handleToggleRawXml()}>
              {rawXml === null ? 'Show raw XML' : 'Hide raw XML'}
            </button>
          ) : null}
          <button type="button" onClick={onClose}>Close</button>
        </div>
      </div>

      <div className="summary-grid">{detailSummaryFields(data)}</div>
      {data.processingError ? <div className="form-error">{data.processingError}</div> : null}
      {actionError ? <div className="form-error">{actionError}</div> : null}

      {detailChildTables?.(data)}

      {hasValidationContent ? (
        <>
          <h3>Validation report</h3>
          <ValidationReportView sections={validationSections} />
        </>
      ) : null}

      {timelineEvents.length > 0 ? (
        <>
          <h3>Timeline</h3>
          <Timeline events={timelineEvents} />
        </>
      ) : null}

      {rawXml !== null ? (
        <>
          <h3>Raw XML</h3>
          <pre className="code-block content-viewer raw-payload-viewer">{rawXml}</pre>
        </>
      ) : null}

      {data.normalizedJson ? (
        <>
          <h3>Normalized JSON</h3>
          <PayloadViewer value={data.normalizedJson} filename={`${data.id}.normalized.json`} />
        </>
      ) : null}

      <ConfirmDialog state={confirm} onClose={() => setConfirm(null)} />
    </section>
  );
}
